import React, { useEffect, useState } from "react";
import i18n from "i18next";
import { fetchWrapper } from "../../../helpers/fetchwrapper";
import { useNavigate, useLocation } from "react-router-dom";
import EditUserPage from "./EditUserPage";
import { baseUrl } from "../../../helpers/constants";
import useWindowSize from "../../../helpers/useWindowSize";
import { StackItemShim, StackShim } from "@fluentui/react-migration-v8-v9";
import {
  Button,
  Combobox,
  Field,
  Input,
  Option,
  Checkbox,
  Link,
  Spinner,
  MessageBar,
  ProgressBar,
  MessageBarBody,
} from "@fluentui/react-components";
import {
  ArrowCounterclockwiseRegular,
  CaretLeftRegular,
  DeleteRegular,
  DismissRegular,
  FilterDismissRegular,
  FilterRegular,
  IosArrowRtl24Regular,
  PersonRegular,
  SaveRegular,
  SearchRegular,
  SendRegular,
} from "@fluentui/react-icons";
import LuebPagedDataGrid from "../../../Controls/LuebPagedDataGrid";
import { IAppUserDto } from "../../../Models/IAppUserDto";
import { UserColumns } from "./UserColumns";
import { IEmail } from "../../../Models/IEmails";
import Header from "../../Layout/Header";
import HeaderTextAnimation from "../../Layout/HeaderTextAnimation";

interface IUsersProps {
  location: any;
}
interface IqueryValues {
  vorname: string;
  nachname: string;
  firma: string;
  eMail: string;
  kundennummer: string;
  stadt: string;
  sprache: string;
  rollen: string[];
  onlyDemo: boolean;
  isDeleted: number;
  IsVerified: number;
  deleteRequested: number;
  hasFaultyRoleAssignment: boolean;
  configuratorButNoTools: number;
  preiseSichtbar: number;
}
function Users(props: IUsersProps) {
  const windowSize = useWindowSize();
  const navigate = useNavigate();
  const location = useLocation();

  const [selectedUser, setSelectedUser] = useState<IAppUserDto>();
  const [selectedEmail, setSelectedEmail] = useState<IEmail>();
  const [emailUndefined, setEmailUndefined] = useState<boolean>(false);

  const [selectedRolesString, setSelectedRolesString] = useState<string>();

  const [getEmail, setGetEmails] = useState<boolean>(false);
  const [getRegisteredProducts, setGetRegisteredProducts] =
    useState<boolean>(false);

  const [deleteUserDialogOpen, setDeleteUserDialogOpen] =
    useState<boolean>(false);
  const [saveEditedUser, setSaveEditedUser] = useState<boolean>(false);
  const [saveButtonDisabled, setSaveButtonDisabled] = useState<boolean>(true);
  const [showSaveMessageBar, setShowSaveMessageBar] = useState<boolean>(false);

  const [selectedEditUserTab, setSelectedEditUserTab] = useState<string>();
  const [sendMail, setSendMail] = useState<boolean>(false);
  const [sendMailButtonDisabled, setSendMailButtonDisabled] =
    useState<boolean>(true);

  const [users, setUsers] = useState<IAppUserDto[]>();
  const [editUser, setEditUser] = useState({ vorname: "", nachname: "" });

  const [loadingUsers, setLoadingUsers] = useState(false);
  const [searched, setSearched] = useState<boolean>(false);
  const [deleteSuccess, setDeleteSuccess] = useState<boolean>(false);
  const [queryValues, setQueryValues] = useState<IqueryValues>({
    vorname: "",
    nachname: "",
    firma: "",
    eMail: "",
    kundennummer: "",
    stadt: "",
    sprache: "",
    rollen: [],
    onlyDemo: false,
    isDeleted: 0,
    IsVerified: 2,
    deleteRequested: 2,
    hasFaultyRoleAssignment: false,
    configuratorButNoTools: 0,
    preiseSichtbar: 2,
  });
  const handleQueryValuesChange = (name: string, val: any) => {
    setSearched(false);
    setQueryValues((prevState) => ({
      ...prevState,
      [name]: val,
    }));
  };

  const [roles, setRoles] = useState<
    {
      concurrencyStamp: string;
      id: string;
      name: string;
      normalizedName: string;
    }[]
  >([]);
  const [currentUserIsAdmin, setCurrentUserIsAdmin] = useState<boolean>();
  const getCurrentUserAdminStatus = () => {
    fetchWrapper
      .getWithAuth(`${baseUrl}/api/account/getmyroles`)
      .then((result) => {
        if (result.includes("Admin")) {
          // Ist Admin!
          setCurrentUserIsAdmin(true);
        } else {
          setCurrentUserIsAdmin(false);
        }
      })
      .catch((error) => {});
  };
  const getRoles = () => {
    fetchWrapper
      .getWithAuth(`${baseUrl}/api/account/getallroles`)
      .then((result) => {
        let options: {
          concurrencyStamp: string;
          id: string;
          name: string;
          normalizedName: string;
        }[] = result;

        setRoles(options);
      })
      .catch((error) => {});
  };

  const getUsersByQuery = (values: any) => {
    setLoadingUsers(true);
    fetchWrapper
      .postWithAuth(`${baseUrl}/api/account/getbyquery`, values)
      .then((result: IAppUserDto[]) => {
        setUsers(result);
      })
      .catch((error) => {})
      .finally(() => {
        setLoadingUsers(false);
      });
  };

  const deleteLockedAccounts = () => {
    fetchWrapper
      .getWithAuth(`${baseUrl}/api/account/DeleteAllLockedAccounts`)
      .then((result) => {
        getUsersByQuery(queryValues);
      })
      .then(() => {
        setDeleteSuccess(true);
        setTimeout(() => {
          setDeleteSuccess(false);
        }, 5000);
      })
      .catch((error) => {});
  };

  const columns = UserColumns({
    small: windowSize.width > 990 ? false : true,
    onSelectedUser: (item) => {
      setSelectedUser(item);
    },
  });

  useEffect(() => {
    var rollenNames: string[] = [];
    roles.map((item) => {
      if (queryValues.rollen.includes(item.id)) {
        rollenNames.push(item.name);
      }
    });

    setSelectedRolesString(rollenNames.join(", "));
  }, [queryValues.rollen]);

  // Initial alle Benutzer und Rollen laden
  useEffect(() => {
    let userName = "";
    if (window.location.search.includes("userNameFilter")) {
      userName = window.location.search.substring(
        window.location.search.indexOf("?userNameFilter=") + 16,
      );
      handleQueryValuesChange("eMail", userName);
    }
    queryValues.eMail = userName;
    getUsersByQuery(queryValues);
    getRoles();
    getCurrentUserAdminStatus();
  }, []);

  useEffect(() => {
    if (selectedUser) {
      navigate("/sales/users?guid=" + selectedUser?.id);
    }
  }, [selectedUser]);

  useEffect(() => {
    if (location.search.includes("guid")) {
      const guid = new URLSearchParams(location.search).get("guid");
      if (guid) {
        fetchWrapper
          .getWithAuth(`${baseUrl}/api/account/getbyguid?guid=${guid}`)
          .then((result: IAppUserDto) => {
            setSelectedUser(result ?? undefined);
          });
      }
    } else {
      setSelectedUser(undefined);
    }
  }, [location.search]);

  useEffect(() => {
    if (saveEditedUser == true) {
      setShowSaveMessageBar(true);
      setTimeout(() => {
        setShowSaveMessageBar(false);
      }, 5000);
    }
  }, [saveEditedUser]);
  return (
    <div>
      <Header
        HTMLElement={
          <>
            <StackShim horizontal verticalAlign="center">
              {(windowSize.width > 1300 ||
                windowSize.width < 1024 ||
                !selectedUser) && (
                <Button
                  icon={<PersonRegular fontSize={24} color="#ff6600" />}
                  size="large"
                  appearance="subtle"
                  onClick={() => {
                    navigate("/sales/users");
                  }}
                >
                  {i18n.t<string>("users").toLocaleUpperCase()}
                </Button>
              )}
              {windowSize.width < 1301 &&
                windowSize.width > 1024 &&
                selectedUser && (
                  <Button
                    style={{ paddingLeft: "0px" }}
                    size="small"
                    onClick={() => {
                      navigate("/sales/users");
                    }}
                    appearance="transparent"
                  >
                    <PersonRegular fontSize={24} color="#ff6600" />
                  </Button>
                )}
              {selectedUser && (
                <div style={{ display: "flex", alignItems: "center" }}>
                  <IosArrowRtl24Regular color="#ff6600" />
                  <Button appearance="subtle">
                    <HeaderTextAnimation
                      text={`${selectedUser.fullName} (${selectedUser.firma})`}
                    />
                  </Button>
                </div>
              )}
            </StackShim>
          </>
        }
      />
      {showSaveMessageBar && (
        <MessageBar intent="success">
          {i18n.t<string>("accountSaved")}
        </MessageBar>
      )}
      {deleteSuccess && (
        <MessageBar intent="success">
          <MessageBarBody>
            {"Alle gesperrten Nutzer wurden gelöscht"}
          </MessageBarBody>
        </MessageBar>
      )}
      <div className="lueb-InnerContentBlockPadding2px">
        {!selectedUser && (
          <>
            <Button
              appearance="transparent"
              onClick={() => {
                getUsersByQuery(queryValues);
                setSearched(true);
              }}
              icon={<SearchRegular />}
            >
              {i18n.t<string>("suchen")}
            </Button>
            <Button
              onClick={() => {
                setQueryValues({
                  vorname: "",
                  nachname: "",
                  firma: "",
                  kundennummer: "",
                  eMail: "",
                  stadt: "",
                  sprache: "",
                  rollen: [],
                  onlyDemo: false,
                  IsVerified: 2,
                  isDeleted: 2,
                  deleteRequested: 2,
                  hasFaultyRoleAssignment: false,
                  preiseSichtbar: 2,
                  configuratorButNoTools: 0,
                });
              }}
              appearance="transparent"
              icon={<FilterDismissRegular />}
            >
              {i18n.t<string>("clearSearchParameter")}
            </Button>
          </>
        )}
        {selectedUser && (
          <>
            {selectedEditUserTab == "editUser" && (
              <>
                <Button
                  appearance="transparent"
                  icon={<SaveRegular />}
                  disabled={saveButtonDisabled}
                  onClick={() => {
                    setSaveEditedUser(true);
                  }}
                >
                  {i18n.t<string>("save")}
                </Button>
                <Button
                  appearance="transparent"
                  disabled={!selectedUser.deleteRequested}
                  icon={<DeleteRegular />}
                  onClick={() => {
                    setDeleteUserDialogOpen(true);
                  }}
                >
                  {i18n.t<string>("deleteUser")}
                </Button>
              </>
            )}
            {selectedEditUserTab == "editUser" && currentUserIsAdmin && (
              <Button
                appearance="transparent"
                icon={<DeleteRegular />}
                onClick={() => {
                  setDeleteUserDialogOpen(true);
                }}
              >
                {i18n.t<string>("deleteUserAdmin")}
              </Button>
            )}
            {selectedEditUserTab == "sendMail" && (
              <Button
                icon={<SendRegular />}
                appearance="transparent"
                disabled={sendMailButtonDisabled}
                onClick={() => {
                  setSendMail(true);
                  setTimeout(() => {
                    setSendMail(false);
                  }, 500);
                }}
              >
                {i18n.t<string>("sendMail")}
              </Button>
            )}
            {selectedEditUserTab == "mails" && selectedEmail != undefined && (
              <Button
                icon={<CaretLeftRegular />}
                onClick={() => {
                  setEmailUndefined(true);
                  setSelectedEmail(undefined);
                  setTimeout(() => {
                    setEmailUndefined(false);
                  }, 300);
                }}
                appearance="transparent"
              >
                Zurück
              </Button>
            )}
            {(selectedEditUserTab == "mails" ||
              selectedEditUserTab == "registeredSystems") && (
              <Button
                icon={<ArrowCounterclockwiseRegular />}
                appearance="transparent"
                onClick={() => {
                  if (selectedEditUserTab == "mails") {
                    setGetEmails(true);
                    setTimeout(() => {
                      setGetEmails(false);
                    }, 300);
                  } else {
                    setGetRegisteredProducts(true);
                    setTimeout(() => {
                      setGetRegisteredProducts(false);
                    }, 300);
                  }
                }}
              >
                {i18n.t<string>("refresh")}
              </Button>
            )}
          </>
        )}
      </div>

      {selectedUser && (
        <>
          {selectedUser.deleteRequested && (
            <MessageBar intent="warning">
              <MessageBarBody>Benutzer hat Löschung beantragt</MessageBarBody>
            </MessageBar>
          )}
          <EditUserPage
            getEmails={getEmail}
            getRegisteredProducts={getRegisteredProducts}
            setEmailUndefined={emailUndefined}
            onEmailSelect={(item: IEmail) => {
              setSelectedEmail(item);
            }}
            disabledSaveButton={(item) => {
              setSaveButtonDisabled(item);
            }}
            saveEditedUser={saveEditedUser}
            onSave={() => {
              navigate("/sales/users");
              setSaveEditedUser(false);
              setTimeout(() => {
                getUsersByQuery(queryValues);
              }, 200);
            }}
            user={selectedUser}
            isDialogDeleteUserOpen={deleteUserDialogOpen}
            closeDialogDeleteUser={() => {
              setDeleteUserDialogOpen(false);
            }}
            sendMail={sendMail}
            sendMailButtonDisabled={(item: boolean) => {
              setSendMailButtonDisabled(item);
            }}
            selectedTabChange={(item: string) => {
              setSelectedEditUserTab(item);
            }}
          />
        </>
      )}
      {!selectedUser && (
        <div className="lueb-ContentBlock">
          <>
            <StackShim horizontal tokens={{ childrenGap: 5 }} wrap={true}>
              <StackItemShim
                grow={1}
                style={{ paddingLeft: 5, paddingRight: 5 }}
              >
                <Field label={i18n.t<string>("firstName")}>
                  <Input
                    contentAfter={
                      queryValues.vorname && (
                        <Button
                          icon={<DismissRegular />}
                          appearance="transparent"
                          onClick={() => {
                            handleQueryValuesChange("vorname", "");
                          }}
                        />
                      )
                    }
                    onKeyDown={(data) => {
                      if (data.key == "Enter") {
                        getUsersByQuery(queryValues);
                      }
                    }}
                    contentBefore={<FilterRegular />}
                    value={queryValues.vorname}
                    onChange={(ev, val) =>
                      handleQueryValuesChange(
                        "vorname",
                        val.value.trimStart() ? val.value.trimStart() : "",
                      )
                    }
                  />
                </Field>
              </StackItemShim>
              <StackItemShim
                grow={1}
                style={{ paddingLeft: 5, paddingRight: 5 }}
              >
                <Field label={i18n.t<string>("lastName")}>
                  <Input
                    contentAfter={
                      queryValues.nachname && (
                        <Button
                          icon={<DismissRegular />}
                          appearance="transparent"
                          onClick={() => {
                            handleQueryValuesChange("nachname", "");
                          }}
                        />
                      )
                    }
                    onKeyDown={(data) => {
                      if (data.key == "Enter") {
                        getUsersByQuery(queryValues);
                      }
                    }}
                    contentBefore={<FilterRegular />}
                    value={queryValues.nachname}
                    onChange={(ev, val) =>
                      handleQueryValuesChange(
                        "nachname",
                        val.value.trimStart() ? val.value.trimStart() : "",
                      )
                    }
                  />
                </Field>
              </StackItemShim>
              <StackItemShim
                grow={1}
                style={{ paddingLeft: 5, paddingRight: 5 }}
              >
                <Field label={i18n.t<string>("company")}>
                  <Input
                    contentAfter={
                      queryValues.firma && (
                        <Button
                          icon={<DismissRegular />}
                          appearance="transparent"
                          onClick={() => {
                            handleQueryValuesChange("firma", "");
                          }}
                        />
                      )
                    }
                    onKeyDown={(data) => {
                      if (data.key == "Enter") {
                        getUsersByQuery(queryValues);
                      }
                    }}
                    contentBefore={<FilterRegular />}
                    value={queryValues.firma}
                    onChange={(ev, val) =>
                      handleQueryValuesChange(
                        "firma",
                        val.value.trimStart() ? val.value.trimStart() : "",
                      )
                    }
                  />
                </Field>
              </StackItemShim>
              <StackItemShim
                grow={1}
                style={{ paddingLeft: 5, paddingRight: 5 }}
              >
                <Field label={"E-Mail"}>
                  <Input
                    contentAfter={
                      queryValues.eMail && (
                        <Button
                          icon={<DismissRegular />}
                          appearance="transparent"
                          onClick={() => {
                            handleQueryValuesChange("eMail", "");
                          }}
                        />
                      )
                    }
                    onKeyDown={(data) => {
                      if (data.key == "Enter") {
                        getUsersByQuery(queryValues);
                      }
                    }}
                    contentBefore={<FilterRegular />}
                    value={queryValues.eMail}
                    onChange={(ev, val) =>
                      handleQueryValuesChange(
                        "eMail",
                        val.value.trimStart() ? val.value.trimStart() : "",
                      )
                    }
                  />
                </Field>
              </StackItemShim>
              <StackItemShim
                grow={1}
                style={{ paddingLeft: 5, paddingRight: 5 }}
              >
                <Field label={"Kundennummer"}>
                  <Input
                    contentAfter={
                      queryValues.kundennummer && (
                        <Button
                          icon={<DismissRegular />}
                          appearance="transparent"
                          onClick={() => {
                            handleQueryValuesChange("kundennummer", "");
                          }}
                        />
                      )
                    }
                    onKeyDown={(data) => {
                      if (data.key == "Enter") {
                        getUsersByQuery(queryValues);
                      }
                    }}
                    contentBefore={<FilterRegular />}
                    value={queryValues.kundennummer}
                    onChange={(ev, val) =>
                      handleQueryValuesChange(
                        "kundennummer",
                        val.value.trimStart() ? val.value.trimStart() : "",
                      )
                    }
                  />
                </Field>
              </StackItemShim>
              <StackItemShim
                grow={1}
                style={{ paddingLeft: 5, paddingRight: 5 }}
              >
                <Field label={i18n.t<string>("city")}>
                  <Input
                    contentAfter={
                      queryValues.stadt && (
                        <Button
                          icon={<DismissRegular />}
                          appearance="transparent"
                          onClick={() => {
                            handleQueryValuesChange("stadt", "");
                          }}
                        />
                      )
                    }
                    onKeyDown={(data) => {
                      if (data.key == "Enter") {
                        getUsersByQuery(queryValues);
                      }
                    }}
                    contentBefore={<FilterRegular />}
                    value={queryValues.stadt}
                    onChange={(ev, val) =>
                      handleQueryValuesChange(
                        "stadt",
                        val.value.trimStart() ? val.value.trimStart() : "",
                      )
                    }
                  />
                </Field>
              </StackItemShim>
              <StackItemShim
                grow={1}
                style={{ paddingLeft: 5, paddingRight: 5 }}
              >
                <Field label={i18n.t<string>("role")}>
                  <Combobox
                    value={selectedRolesString}
                    multiselect
                    onOptionSelect={(ev, data) => {
                      if (
                        data.optionValue != undefined &&
                        data.optionText != undefined &&
                        data.selectedOptions != undefined
                      ) {
                        if (!queryValues.rollen.includes(data.optionValue)) {
                          handleQueryValuesChange("rollen", [
                            ...queryValues.rollen,
                            data.optionValue as string,
                          ]);
                        } else if (
                          queryValues.rollen.includes(data.optionValue)
                        ) {
                          handleQueryValuesChange(
                            "rollen",
                            queryValues.rollen.filter(
                              (key) => key != data.optionValue,
                            ),
                          );
                        }
                      }
                    }}
                    selectedOptions={queryValues.rollen}
                  >
                    {roles.map(
                      (role: {
                        concurrencyStamp: string;
                        id: string;
                        name: string;
                        normalizedName: string;
                      }) => {
                        return (
                          <Option value={role.id}>
                            {role.name ? role.name : "Falsch"}
                          </Option>
                        );
                      },
                    )}
                  </Combobox>
                </Field>
              </StackItemShim>
            </StackShim>
            <StackShim horizontal tokens={{ childrenGap: 5 }} wrap={true}>
              <StackItemShim
                grow={1}
                style={{ paddingLeft: 5, paddingRight: 5 }}
              >
                <Checkbox
                  label="Löschung beantragt"
                  checked={
                    queryValues.deleteRequested == 1
                      ? true
                      : queryValues.deleteRequested == 2
                        ? "mixed"
                        : false
                  }
                  onChange={(ev, data) => {
                    let val = 2;

                    if (queryValues.deleteRequested == 0) {
                      val = 1;
                    } else if (queryValues.deleteRequested == 1) {
                      val = 2;
                    } else if (queryValues.deleteRequested == 2) {
                      val = 0;
                    }
                    handleQueryValuesChange("deleteRequested", val);
                  }}
                ></Checkbox>
              </StackItemShim>

              <StackItemShim
                grow={1}
                style={{ paddingLeft: 5, paddingRight: 5 }}
              >
                <Checkbox
                  label="Gesperrt"
                  checked={
                    queryValues.isDeleted == 1
                      ? true
                      : queryValues.isDeleted == 2
                        ? "mixed"
                        : false
                  }
                  onChange={(ev, data) => {
                    let val = 2;

                    if (queryValues.isDeleted == 0) {
                      val = 1;
                    } else if (queryValues.isDeleted == 1) {
                      val = 2;
                    } else if (queryValues.isDeleted == 2) {
                      val = 0;
                    }
                    handleQueryValuesChange("isDeleted", val);
                  }}
                ></Checkbox>
              </StackItemShim>

              <StackItemShim
                grow={1}
                style={{ paddingLeft: 5, paddingRight: 5 }}
              >
                <Checkbox
                  label="E-Mail bestätigt"
                  checked={
                    queryValues.IsVerified == 1
                      ? true
                      : queryValues.IsVerified == 2
                        ? "mixed"
                        : false
                  }
                  onChange={(ev, data) => {
                    let val = 2;

                    if (queryValues.IsVerified == 0) {
                      val = 1;
                    } else if (queryValues.IsVerified == 1) {
                      val = 2;
                    } else if (queryValues.IsVerified == 2) {
                      val = 0;
                    }
                    handleQueryValuesChange("IsVerified", val);
                  }}
                ></Checkbox>
              </StackItemShim>

              <StackItemShim
                grow={1}
                style={{ paddingLeft: 5, paddingRight: 5 }}
              >
                <Checkbox
                  label="Nur Demo"
                  checked={queryValues.onlyDemo}
                  onChange={(ev, data) => {
                    handleQueryValuesChange("onlyDemo", data.checked);
                  }}
                ></Checkbox>
              </StackItemShim>

              <StackItemShim
                grow={1}
                style={{ paddingLeft: 5, paddingRight: 5 }}
              >
                <Checkbox
                  label="Fehlerhafte Rollenzuweisung"
                  checked={queryValues.hasFaultyRoleAssignment}
                  onChange={(ev, data) => {
                    handleQueryValuesChange(
                      "hasFaultyRoleAssignment",
                      data.checked,
                    );
                  }}
                />
              </StackItemShim>

              <StackItemShim
                grow={1}
                style={{ paddingLeft: 5, paddingRight: 5 }}
              >
                <Checkbox
                  label="Configurator ohne Tools"
                  checked={
                    queryValues.configuratorButNoTools == 0 ? false : true
                  }
                  onChange={(ev, data) => {
                    handleQueryValuesChange(
                      "configuratorButNoTools",
                      data.checked ? 1 : 0,
                    );
                  }}
                />
              </StackItemShim>
              <StackItemShim
                grow={1}
                style={{ paddingLeft: 5, paddingRight: 5 }}
              >
                <Checkbox
                  label="Preise sichtbar"
                  checked={queryValues.preiseSichtbar == 1}
                  onChange={(ev, data) => {
                    let val = 2;
                    if (!data.checked && queryValues.IsVerified == 2) val = 0;
                    else if (!data.checked && queryValues.IsVerified == 1)
                      val = 2;
                    else val = 1;
                    handleQueryValuesChange("preiseSichtbar", val);
                  }}
                />
              </StackItemShim>
            </StackShim>
          </>
        </div>
      )}
      {!selectedUser && (
        <>
          {loadingUsers && <ProgressBar thickness="large" />}
          {users != undefined && !loadingUsers && (
            <div className="lueb-ContentBlock">
              <LuebPagedDataGrid
                sortable
                columns={columns}
                items={users}
                resizableColumns={true}
                columnSizingOptions={{
                  email: { defaultWidth: 270, minWidth: 200 },
                  vorname: { defaultWidth: 100, minWidth: 70 },
                  nachname: { defaultWidth: 100, minWidth: 70 },
                  stadt: { defaultWidth: 150, minWidth: 100 },
                  firma: { defaultWidth: 210, minWidth: 100 },
                  registrationDate: { defaultWidth: 120, minWidth: 80 },
                }}
              />
              {queryValues.isDeleted == 1 &&
                searched &&
                users.length != 0 &&
                currentUserIsAdmin && (
                  <div style={{ display: "flex", justifyContent: "flex-end" }}>
                    <Button
                      appearance="primary"
                      onClick={() => {
                        console.log("Test");
                        deleteLockedAccounts();
                      }}
                    >
                      Alle geperrten Benutzer löschen
                    </Button>
                  </div>
                )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default Users;
